import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook that triggers a callback after a specified period of user inactivity.
 * Tracks mouse movement, clicks, keyboard input, touch, and scroll events.
 *
 * @param onInactive - Callback fired when the inactivity timeout expires
 * @param timeoutMs - Inactivity threshold in milliseconds (default: 60000 = 60s)
 */
export function useInactivityTimer(
  onInactive: () => void,
  timeoutMs: number = 60_000
) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callbackRef = useRef(onInactive);

  // Keep callback ref up-to-date without restarting listeners
  useEffect(() => {
    callbackRef.current = onInactive;
  }, [onInactive]);

  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      callbackRef.current();
    }, timeoutMs);
  }, [timeoutMs]);

  useEffect(() => {
    const activityEvents = [
      'mousemove',
      'mousedown',
      'keydown',
      'touchstart',
      'scroll',
      'click',
    ];

    // Start the timer immediately
    resetTimer();

    // Reset on any user activity
    const handleActivity = () => resetTimer();
    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [resetTimer]);
}
